/**
 * <brick-lightbox> Full-screen image viewer for the product gallery.
 * Loaded (defer) by sections/main-product only when "Enable zoom" is on.
 * <dialog> + showModal (inert background), focus trap, Esc / backdrop close,
 * arrow keys, swipe (scroll-snap), click-to-zoom with pointer panning,
 * focus restored to the image that opened it.
 */
(() => {
  if (customElements.get('brick-lightbox')) return;

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const motionOff = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class BrickLightbox extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.track = this.querySelector('[data-lightbox-track]');
      if (!this.dialog || !this.track) return;
      this.slides = Array.from(this.track.children);
      this.counter = this.querySelector('[data-lightbox-counter]');
      this.index = 0;

      this.querySelector('[data-lightbox-prev]')?.addEventListener('click', () => this.go(this.index - 1));
      this.querySelector('[data-lightbox-next]')?.addEventListener('click', () => this.go(this.index + 1));
      this.querySelector('[data-lightbox-close]')?.addEventListener('click', () => this.close());

      if (this.slides.length < 2) this.classList.add('is-single');

      this.dialog.addEventListener('keydown', (event) => this.onKeydown(event));
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });
      this.dialog.addEventListener('close', () => {
        this.resetZoom();
        document.documentElement.classList.remove('has-lightbox');
        if (this.opener && this.opener.isConnected) this.opener.focus({ preventScroll: true });
      });
      // Clicks on the bare backdrop area (outside slides / controls) close.
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog || event.target === this.track || event.target.classList.contains('lightbox__slide')) {
          this.close();
        }
      });

      this.slides.forEach((slide) => {
        const button = slide.querySelector('.lightbox__zoom');
        if (!button) return;
        button.addEventListener('click', (event) => this.toggleZoom(button, event));
        button.addEventListener('pointermove', (event) => {
          if (button.getAttribute('aria-pressed') === 'true') this.setOrigin(button, event);
        });
      });

      this.track.addEventListener(
        'scroll',
        () => {
          cancelAnimationFrame(this.frame);
          this.frame = requestAnimationFrame(() => {
            const width = this.track.clientWidth || 1;
            const next = Math.round(this.track.scrollLeft / width);
            if (next !== this.index) {
              this.index = next;
              this.resetZoom();
              this.updateCounter();
            }
          });
        },
        { passive: true }
      );
    }

    openAt(mediaId, opener) {
      if (!this.dialog) return;
      this.opener = opener || document.activeElement;
      const index = Math.max(
        0,
        this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId))
      );
      // Lazy images inside a closed dialog never start loading: kick them.
      this.slides.forEach((slide) => slide.querySelector('img')?.setAttribute('loading', 'eager'));
      if (!this.dialog.open) this.dialog.showModal();
      document.documentElement.classList.add('has-lightbox');
      this.go(index, 'auto');
      this.querySelector('[data-lightbox-close]')?.focus({ preventScroll: true });
    }

    close() {
      if (this.dialog && this.dialog.open) this.dialog.close();
    }

    go(index, behavior) {
      if (!this.slides.length) return;
      this.index = (index + this.slides.length) % this.slides.length;
      this.resetZoom();
      this.track.scrollTo({
        left: this.slides[this.index].offsetLeft,
        behavior: behavior || (motionOff() ? 'auto' : 'smooth'),
      });
      this.updateCounter();
    }

    updateCounter() {
      if (!this.counter) return;
      const pad = (n) => String(n).padStart(2, '0');
      this.counter.textContent = `${pad(this.index + 1)} / ${pad(this.slides.length)}`;
    }

    onKeydown(event) {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.go(this.index + 1);
        return;
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.go(this.index - 1);
        return;
      }
      if (event.key !== 'Tab') return;
      const items = Array.from(this.dialog.querySelectorAll(FOCUSABLE)).filter((el) => {
        if (!el.getClientRects().length) return false;
        const slide = el.closest('.lightbox__slide');
        return !slide || slide === this.slides[this.index];
      });
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && (document.activeElement === first || !this.dialog.contains(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    setOrigin(button, event) {
      const rect = button.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 100;
      const y = ((event.clientY - rect.top) / rect.height) * 100;
      button.style.setProperty('--zoom-x', `${Math.max(0, Math.min(100, x)).toFixed(1)}%`);
      button.style.setProperty('--zoom-y', `${Math.max(0, Math.min(100, y)).toFixed(1)}%`);
    }

    toggleZoom(button, event) {
      const zoomed = button.getAttribute('aria-pressed') !== 'true';
      if (zoomed && event.clientX) this.setOrigin(button, event);
      else if (zoomed) {
        button.style.setProperty('--zoom-x', '50%');
        button.style.setProperty('--zoom-y', '50%');
      }
      button.setAttribute('aria-pressed', String(zoomed));
      this.dialog.classList.toggle('is-zoomed', zoomed);
    }

    resetZoom() {
      if (!this.dialog) return;
      this.dialog.classList.remove('is-zoomed');
      (this.slides || []).forEach((slide) => {
        slide.querySelector('.lightbox__zoom')?.setAttribute('aria-pressed', 'false');
      });
    }
  }

  customElements.define('brick-lightbox', BrickLightbox);
})();
